import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { AvailabilityToggle } from "./components/AvailabilityToggle";
import { CollectionPills } from "./components/CollectionPills";
import { FilterButton } from "./components/FilterButton";
import { PriceFilterModal } from "./components/PriceFilterModal";
import { ProductDetail } from "./components/ProductDetail";
import { TypeTagBar } from "./components/TypeTagBar";
import { ProductGrid } from "./components/ProductGrid";
import { SearchBar } from "./components/SearchBar";
import { SnackbarProvider } from "./components/Snackbar";
import { SortDropdown } from "./components/SortDropdown";
import { useDebounce } from "./hooks/useDebounce";
import { useTypeTagCounts } from "./hooks/useTypeTagCounts";
import { useVendorCollections } from "./hooks/useVendorCollections";
import { useVendorProducts } from "./hooks/useVendorProducts";
import {
  readCachedGroupHandle,
  writeCachedGroupHandle,
} from "./lib/group-cache";
import { readPocaUrlFilters, writePocaUrlFilters } from "./lib/url-filters";
import type { PocaConfig, SortOption } from "./types";

const POCA_URL_PRODUCT = "product";

function readProductHandleFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get(POCA_URL_PRODUCT) || null;
}

type Props = {
  config: PocaConfig;
};

export function App({ config }: Props) {
  // In-page router: ?product={handle} renders the detail view
  const [productHandle, setProductHandle] = useState<string | null>(
    () => readProductHandleFromUrl()
  );
  const savedScrollYRef = useRef(0);

  useEffect(() => {
    const onPopState = () => {
      setProductHandle(readProductHandleFromUrl());
      window.scrollTo({ top: 0 });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigateToDetail = useCallback((handle: string) => {
    savedScrollYRef.current = window.scrollY;
    const url = new URL(window.location.href);
    url.searchParams.set(POCA_URL_PRODUCT, handle);
    window.history.pushState(null, "", url.toString());
    setProductHandle(handle);
    window.scrollTo({ top: 0 });
  }, []);

  const navigateBack = useCallback(() => {
    const scrollTarget = savedScrollYRef.current;
    const url = new URL(window.location.href);
    url.searchParams.delete(POCA_URL_PRODUCT);
    window.history.pushState(null, "", url.toString());
    // flushSync: force React to render the list synchronously before scrolling
    flushSync(() => setProductHandle(null));
    window.scrollTo({ top: scrollTarget, behavior: "instant" });
  }, []);

  const initialUrl = useMemo(() => readPocaUrlFilters(), []);
  const initialGroupHandle = useMemo(
    () => initialUrl.groupHandle ?? readCachedGroupHandle(),
    [initialUrl.groupHandle]
  );

  const [search, setSearch] = useState(initialUrl.search);
  const debouncedSearch = useDebounce(search, 300);
  const [sort, setSort] = useState<SortOption>(initialUrl.sort);
  const [minPrice, setMinPrice] = useState<number | null>(initialUrl.minPrice);
  const [maxPrice, setMaxPrice] = useState<number | null>(initialUrl.maxPrice);
  const [availableOnly, setAvailableOnly] = useState(true);
  const [selectedCollectionId, setSelectedCollectionId] = useState<
    string | null
  >(null);
  const [urlGroupHandle, setUrlGroupHandle] = useState<string | null>(
    initialGroupHandle
  );
  const [selectedMemberTag, setSelectedMemberTag] = useState<string | null>(
    initialUrl.member
  );
  const [selectedProductTags, setSelectedProductTags] = useState<string[]>(
    initialUrl.tags
  );
  const [priceModalOpen, setPriceModalOpen] = useState(false);

  const {
    collections,
    loading: collectionsLoading,
  } = useVendorCollections(config);

  useEffect(() => {
    if (collectionsLoading || collections.length === 0 || !urlGroupHandle) {
      return;
    }
    const match = collections.find((c) => c.handle === urlGroupHandle);
    if (match) {
      setSelectedCollectionId(match.id);
    } else {
      setUrlGroupHandle(null);
      setSelectedMemberTag(null);
      setSelectedProductTags([]);
      writeCachedGroupHandle(null);
    }
  }, [collectionsLoading, collections, urlGroupHandle]);

  const selectedCollectionHandle = useMemo(() => {
    if (selectedCollectionId) {
      return (
        collections.find((c) => c.id === selectedCollectionId)?.handle ??
        urlGroupHandle
      );
    }
    return urlGroupHandle;
  }, [selectedCollectionId, collections, urlGroupHandle]);

  useEffect(() => {
    if (productHandle) return; // don't overwrite ?product= while in detail view
    writePocaUrlFilters({
      groupHandle: selectedCollectionHandle,
      member: selectedMemberTag,
      search: debouncedSearch,
      minPrice,
      maxPrice,
      sort,
      tags: selectedProductTags,
    });
  }, [
    productHandle,
    selectedCollectionHandle,
    selectedMemberTag,
    debouncedSearch,
    minPrice,
    maxPrice,
    sort,
    selectedProductTags,
  ]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setProductHandle(readProductHandleFromUrl());
      const fromUrl = readPocaUrlFilters();
      setSearch(fromUrl.search);
      setSort(fromUrl.sort);
      setMinPrice(fromUrl.minPrice);
      setMaxPrice(fromUrl.maxPrice);
      setUrlGroupHandle(fromUrl.groupHandle);
      setSelectedMemberTag(fromUrl.member);
      setSelectedProductTags(fromUrl.tags);
      if (!fromUrl.groupHandle) {
        setSelectedCollectionId(null);
        return;
      }
      const match = collections.find((c) => c.handle === fromUrl.groupHandle);
      setSelectedCollectionId(match?.id ?? null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [collections]);

  const handleCollectionSelect = (id: string | null) => {
    setSelectedCollectionId(id);
    const handle = id
      ? (collections.find((c) => c.id === id)?.handle ?? null)
      : null;
    setUrlGroupHandle(handle);
    writeCachedGroupHandle(handle);
    setSelectedMemberTag(null);
    // 종류(TYPE) 필터는 그룹 안에서만 — ALL 로 나가면 같이 푼다 (안 보이는 필터가 남지 않게)
    if (!id) setSelectedProductTags([]);
  };

  const handleMemberSelect = (tag: string | null) => {
    setSelectedMemberTag(tag);
  };

  const filters = useMemo(
    () => ({
      search: debouncedSearch,
      minPrice,
      maxPrice,
      availableOnly,
      sortKey: sort.key,
      reverse: sort.reverse,
      collectionId: selectedCollectionId,
      collectionHandle: selectedCollectionHandle,
      memberTag: selectedMemberTag,
      productTags: selectedCollectionId ? selectedProductTags : [],
    }),
    [
      debouncedSearch,
      minPrice,
      maxPrice,
      availableOnly,
      sort,
      selectedCollectionId,
      selectedCollectionHandle,
      selectedMemberTag,
      selectedProductTags,
    ]
  );

  const { products, pageInfo, loading, loadingMore, error, loadMore } =
    useVendorProducts(config, filters);

  // TYPE 줄 건수 — 그룹을 골랐을 때만 (ALL 에선 줄 자체를 안 보여주므로 5,000개 넘는 전체 집계도 안 한다).
  // 상세 화면에서도 안 받는다. (훅은 아래 early return 보다 먼저 호출돼야 한다 — 순서가 바뀌면 React #300)
  const typeTags = useTypeTagCounts(config, {
    collectionId: selectedCollectionId,
    memberTag: selectedMemberTag,
    availableOnly,
    enabled: !productHandle && selectedCollectionId != null,
  });

  const activeFilterCount =
    config.enablePriceFilter && (minPrice != null || maxPrice != null) ? 1 : 0;

  if (productHandle) {
    return (
      <SnackbarProvider>
        <section className="poca-app">
          <ProductDetail handle={productHandle} config={config} onBack={navigateBack} onNavigate={navigateToDetail} />
        </section>
      </SnackbarProvider>
    );
  }

  return (
    <SnackbarProvider>
    <section className="poca-app">
      <div className="poca-page-header">
        <span className="poca-page-header__eyebrow">✦ Photocard Collection ✦</span>
        <h1 className="poca-page-header__title">Hallyu Superstore's Poca Vault</h1>
      </div>

      <CollectionPills
        collections={collections}
        selectedId={selectedCollectionId}
        onSelect={handleCollectionSelect}
        selectedMemberTag={selectedMemberTag}
        onMemberSelect={handleMemberSelect}
        titlePrefix={config.collectionTitlePrefix}
        loading={collectionsLoading}
      />

      <header className="poca-toolbar">
        <div className="poca-toolbar__search">
          {config.enableSearch && (
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search your favorite photocards"
            />
          )}
          {config.enablePriceFilter && (
            <FilterButton
              activeCount={activeFilterCount}
              onClick={() => setPriceModalOpen(true)}
              ariaExpanded={priceModalOpen}
            />
          )}
        </div>
        <div className="poca-toolbar__filters">
          {(config.enableAvailabilityFilter || config.enableSort) && (
            <div className="poca-toolbar__controls">
              {config.enableAvailabilityFilter && (
                <AvailabilityToggle
                  value={availableOnly}
                  onChange={setAvailableOnly}
                />
              )}
              {config.enableSort && (
                <SortDropdown value={sort} onChange={setSort} />
              )}
            </div>
          )}
        </div>
      </header>

      {selectedCollectionId != null && (
        <TypeTagBar
          counts={typeTags.counts}
          total={typeTags.total}
          loading={typeTags.loading}
          selected={selectedProductTags}
          onChange={setSelectedProductTags}
        />
      )}

      {error && (
        <div className="poca-alert poca-alert--error" role="alert">
          {error}
        </div>
      )}

      {!loading && products.length > 0 && (
        <p className="poca-results-meta" aria-live="polite">
          {products.length}
          {pageInfo.hasNextPage ? "+" : ""} items
        </p>
      )}

      {loading ? (
        <div className="poca-state poca-state--loading">Loading…</div>
      ) : products.length === 0 ? (
        <EmptyState
          hasFilters={
            !!debouncedSearch ||
            minPrice != null ||
            maxPrice != null ||
            availableOnly ||
            selectedCollectionId != null ||
            selectedMemberTag != null ||
            selectedProductTags.length > 0
          }
        />
      ) : (
        <ProductGrid products={products} config={config} onNavigate={navigateToDetail} />
      )}

      {config.enablePriceFilter && (
        <PriceFilterModal
          open={priceModalOpen}
          appliedMinPrice={minPrice}
          appliedMaxPrice={maxPrice}
          onClose={() => setPriceModalOpen(false)}
          onApply={({ minPrice: mn, maxPrice: mx }) => {
            setMinPrice(mn);
            setMaxPrice(mx);
          }}
        />
      )}

      {pageInfo.hasNextPage && (
        <div className="poca-loadmore">
          <button
            type="button"
            className="poca-loadmore__btn"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </section>
    </SnackbarProvider>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  if (hasFilters) {
    return (
      <div className="poca-state poca-state--empty">
        <p>No products match your current filters.</p>
      </div>
    );
  }
  return (
    <div className="poca-state poca-state--empty">
      <p>No products found.</p>
    </div>
  );
}
